<template>
  <div class="shipping-viability px-4 mb-4">
    <div class="has-text-weight-medium mb-2">Consulte a entrega para sua região</div>

    <form
      v-if="mode === 'cep'"
      class="is-flex is-align-items-center mb-0"
      @submit.prevent="checkByCep"
    >
      <input
        v-model="cep"
        v-mask="'#####-###'"
        class="input is-rounded is-flex-grow-1"
        type="text"
        inputmode="numeric"
        placeholder="00000-000"
      >
      <button class="button is-primary is-rounded ml-2 keep-modal-open" type="submit" :class="{ 'is-loading': loading }">
        Consultar
      </button>
    </form>

    <form
      v-else
      class="is-flex is-align-items-center mb-0"
      @submit.prevent="checkByUf"
    >
      <div class="select is-rounded is-fullwidth is-flex-grow-1">
        <select v-model="selectedUf">
          <option value="" disabled>Selecione seu estado</option>
          <option v-for="state in sortedStates" :key="state.uf" :value="state.uf">
            {{ state.name }}
          </option>
        </select>
      </div>
      <button class="button is-primary is-rounded ml-2 keep-modal-open" type="submit" :class="{ 'is-loading': loading }">
        Consultar
      </button>
    </form>

    <a class="is-size-7 keep-modal-open" @click="toggleMode">
      {{ mode === 'cep' ? 'Não sei meu CEP, consultar por estado' : 'Consultar por CEP' }}
    </a>

    <div
      v-if="verdict"
      class="mt-2 has-text-weight-medium"
      :class="verdictClass"
    >
      {{ verdict.message }}
    </div>
  </div>
</template>

<script>
import { ref, computed } from "vue";
import { mask } from "vue-the-mask";
import {
  checkViabilityByCep,
  checkViabilityByUf,
} from "services/cep/checkShippingViability";

export default {
  directives: { mask },
  props: {
    viabilityUrl: { type: String, required: true },
    states: { type: Object, required: true },
    initialCep: { type: String, default: "" },
    page: { type: String, default: "Product" },
    mixpanelData: { type: Object, default: () => ({}) },
  },
  setup(props) {
    const mode = ref("cep");
    const cep = ref(props.initialCep || "");
    const selectedUf = ref("");
    const verdict = ref(null);
    const loading = ref(false);

    const sortedStates = computed(() =>
      Object.entries(props.states)
        .map(([uf, name]) => ({ uf, name }))
        .sort((a, b) => a.name.localeCompare(b.name, "pt-BR")),
    );

    const toggleMode = () => {
      mode.value = mode.value === "cep" ? "uf" : "cep";
      verdict.value = null;
    };

    const applyResult = (result) => {
      verdict.value = {
        message: result.message,
        status: result.status,
        viable: !!result.viable,
      };

      if (result.ok) track(result.uf, result.viable);
    };

    const verdictClass = computed(() => {
      if (!verdict.value) return "";
      if (verdict.value.status === "on_request") return "has-text-grey-dark";

      return verdict.value.viable ? "has-text-success" : "has-text-danger";
    });

    const runCheck = (promise) => {
      loading.value = true;

      promise.then(applyResult).finally(() => {
        loading.value = false;
      });
    };

    const checkByCep = () => {
      runCheck(checkViabilityByCep(props.viabilityUrl, cep.value));
    };

    const checkByUf = () => {
      if (!selectedUf.value) {
        verdict.value = { message: "Selecione um estado.", viable: false };
        return;
      }

      runCheck(checkViabilityByUf(props.viabilityUrl, selectedUf.value));
    };

    const track = (uf, viable) => {
      mixpanel.track("Shipping Viability Checked", {
        Page: props.page,
        UF: uf,
        Viable: !!viable,
        ...props.mixpanelData,
      });
    };

    return {
      mode,
      cep,
      sortedStates,
      selectedUf,
      verdict,
      verdictClass,
      loading,
      toggleMode,
      checkByCep,
      checkByUf,
    };
  },
};
</script>
